/**
 * Utility functions for managing trial reminder display
 */

const TRIAL_REMINDER_KEY = "trialReminderShown";

/**
 * Check if trial reminder should be displayed
 * @param {Object} subscription - The user's subscription object
 * @returns {Object|null} - Returns reminder details or null if shouldn't show
 */
export const shouldShowTrialReminder = (subscription) => {
  // Check if reminder was already shown this session
  const reminderShown = sessionStorage.getItem(TRIAL_REMINDER_KEY);
  if (reminderShown === "true") {
    return null;
  }

  // Check if subscription is in trial period
  if (!subscription || !subscription.isTrial) {
    return null;
  }

  // Get trial end date
  const trialEndDate = subscription.trialEndDate || subscription.endDate;
  if (!trialEndDate) {
    return null;
  }

  // Calculate days remaining
  const now = new Date();
  now.setHours(0, 0, 0, 0); // Normalize to start of day
  const endDate = new Date(trialEndDate);
  endDate.setHours(0, 0, 0, 0); // Normalize to start of day
  const timeDiff = endDate.getTime() - now.getTime();
  const daysRemaining = Math.round(timeDiff / (1000 * 60 * 60 * 24));

  // Show reminder if between 1 and 10 days remain
  if (daysRemaining >= 1 && daysRemaining <= 10) {
    return {
      trialEndDate: trialEndDate,
      daysRemaining: daysRemaining,
    };
  }

  return null;
};

/**
 * Mark trial reminder as shown for this session
 */
export const markTrialReminderShown = () => {
  sessionStorage.setItem(TRIAL_REMINDER_KEY, "true");
};

/**
 * Clear trial reminder flag (called on logout or session cleanup)
 */
export const clearTrialReminderFlag = () => {
  sessionStorage.removeItem(TRIAL_REMINDER_KEY);
};

/**
 * Check if user is in trial period (regardless of reminder timing)
 * @param {Object} subscription - The user's subscription object
 * @returns {boolean}
 */
export const isInTrialPeriod = (subscription) => {
  if (!subscription || !subscription.isTrial) {
    return false;
  }

  const trialEndDate = subscription.trialEndDate || subscription.endDate;
  if (!trialEndDate) {
    return false;
  }

  const now = new Date();
  const endDate = new Date(trialEndDate);
  return now < endDate;
};
